import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import Spinner from "components/spinner";
import Table, { useTable } from "components/table";
import { Status } from "components/status";
import { useModalContext } from "components/modal";
import Placeholder from "views/request/component/placeholder";
import { formatPhoneNumber } from "utils/phoneNumberFormatter";
import { getCustomerCommunications } from "./api";
import CommunicationDetailModal from "./CommunicationDetailModal";
import { formatCommunicationPreview, htmlToPlainText } from "./messagePreview";
import { getLinkedRecordPath } from "./linkedRecordPath";
import {
  formatTrackingValue,
  getTrackingColumnLabel,
} from "./trackingDisplay";
import {
  CommunicationChannel,
  CustomerCommunicationListItem,
  StatusFilter,
} from "./types";

const STATUS_COLORS: Record<string, string> = {
  sent: "0,200,100",
  failed: "239,68,68",
  queued: "234,179,8",
  pending: "160,160,160",
};

type Props = {
  customerId: string;
  channel: CommunicationChannel;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "--";
  return new Date(dateString).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function CommunicationsList({ customerId, channel }: Props) {
  const { t } = useTranslation();
  const tBase = "views.customer.containers.communications";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const modal = useModalContext();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const table = useTable();

  const listQuery = useQuery(
    [
      "customer-communications",
      customerId,
      channel,
      table.pageIndex,
      statusFilter,
    ],
    () =>
      getCustomerCommunications(customerId, {
        channel,
        page: table.pageIndex,
        limit: 25,
        status: statusFilter,
      }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        table.setPageCount(data.pageCount || 1);
      },
    }
  );

  const openDetail = (row: CustomerCommunicationListItem) => {
    modal.setModal({
      label: channel === "email" ? tr("Email Details") : tr("Text Details"),
      component: (
        <CommunicationDetailModal
          customerId={customerId}
          channel={channel}
          listItem={row}
          onClose={() => modal.setModal(undefined)}
        />
      ),
      props: { modalWidth: "720px" },
    });
  };

  const columns = {
    recipient: {
      label: channel === "email" ? tr("Email") : tr("Phone"),
      disableSort: true,
      width: channel === "email" ? "12%" : "14%",
      cell: (row: CustomerCommunicationListItem) => (
        <span className="normal-case block truncate">
          {channel === "email"
            ? row.destinationEmail || "--"
            : formatPhoneNumber(row.destinationPhone) ||
              row.destinationPhone ||
              "--"}
        </span>
      ),
    },
    ...(channel === "email"
      ? {
          subject: {
            label: tr("Subject"),
            disableSort: true,
            width: "16%",
            cell: (row: CustomerCommunicationListItem) => (
              <span
                className="normal-case block truncate"
                title={row.subject || undefined}
              >
                {row.subject || "--"}
              </span>
            ),
          },
        }
      : {}),
    preview: {
      label: tr("Message"),
      disableSort: true,
      width: channel === "email" ? "20%" : "26%",
      cell: (row: CustomerCommunicationListItem) => (
        <span
          className="normal-case text-gray-600 block truncate"
          title={htmlToPlainText(row.messagePreview || "")}
        >
          {formatCommunicationPreview(row.messagePreview)}
        </span>
      ),
    },
    linkedRecord: {
      label: tr("Linked Record"),
      disableSort: true,
      width: "12%",
      disableRowClick: true,
      cell: (row: CustomerCommunicationListItem) => {
        if (!row.linkedRecord) return "--";
        const path = getLinkedRecordPath(row.linkedRecord);
        if (path) {
          return (
            <Link
              to={path}
              className="text-[#ff4f00] hover:underline block truncate normal-case"
              title={row.linkedRecord.label}
              onClick={(e) => e.stopPropagation()}
            >
              {row.linkedRecord.label}
            </Link>
          );
        }
        return (
          <span className="block truncate normal-case" title={row.linkedRecord.label}>
            {row.linkedRecord.label}
          </span>
        );
      },
    },
    sentAt: {
      label: tr("Date Sent"),
      disableSort: true,
      width: "14%",
      cell: (row: CustomerCommunicationListItem) => (
        <span className="normal-case block truncate">{formatDate(row.sentAt)}</span>
      ),
    },
    tracking: {
      label: getTrackingColumnLabel(channel, tr),
      disableSort: true,
      width: "12%",
      cell: (row: CustomerCommunicationListItem) => (
        <span className="normal-case block truncate">
          {formatTrackingValue(row, tr)}
        </span>
      ),
    },
    status: {
      label: tr("Status"),
      disableSort: true,
      width: "8%",
      cell: (row: CustomerCommunicationListItem) => (
        <Status color={STATUS_COLORS[row.status] || "160,160,160"}>
          {row.status}
        </Status>
      ),
    },
  };

  if (listQuery.isLoading) return <Spinner />;

  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="py-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500">
          {t(`${tBase}.Showing count`, {
            count: listQuery.data?.totalCount ?? 0,
          })}
        </p>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">{tr("Filter status")}</span>
          <select
            className="border rounded px-2 py-1"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              table.handlePage(1);
            }}
          >
            <option value="all">{tr("All")}</option>
            <option value="sent">{tr("Sent")}</option>
            <option value="failed">{tr("Failed")}</option>
          </select>
        </label>
      </div>

      {rows.length === 0 ? (
        <Placeholder
          type="note"
          header={
            channel === "email" ? tr("No emails yet") : tr("No texts yet")
          }
          label={tr("Communications will appear here when sent.")}
        />
      ) : (
        <div className="w-full max-w-full">
          <Table
            data={rows}
            columns={columns}
            handleClick={openDetail}
            pageCount={table.pageCount}
            pageIndex={table.pageIndex}
            handlePage={table.handlePage}
            fixedColumnWidths
          />
        </div>
      )}
    </div>
  );
}
